import React, { useCallback, useEffect, useState, createContext, useContext } from 'react';
import { teamsApi, usersApi, rolesApi, TeamRecord, UserRecord, RoleRecord } from '../services/creatorApi';
// ── Permission system ──
export type Permission = 'forms.view' | 'forms.create' | 'forms.edit' | 'forms.delete' | 'templates.view' | 'templates.create' | 'templates.edit' | 'templates.delete' | 'lists.view' | 'lists.create' | 'lists.edit' | 'lists.delete' | 'lists.manage_intake' | 'campaigns.view' | 'campaigns.create' | 'campaigns.edit' | 'campaigns.send' | 'campaigns.delete' | 'submissions.view' | 'submissions.export' | 'subscriptions.view' | 'subscriptions.manage' | 'subscriptions.export' | 'contacts.view' | 'contacts.create' | 'contacts.edit' | 'contacts.delete' | 'contacts.export' | 'tags.view' | 'tags.create' | 'tags.edit' | 'tags.delete' | 'configs.view' | 'configs.create' | 'configs.edit' | 'configs.delete' | 'users.view' | 'users.invite' | 'users.edit' | 'users.delete' | 'users.manage_roles' | 'settings.view' | 'settings.edit' | 'audit.view' | 'audit.export' | 'roles.view' | 'roles.create' | 'roles.edit' | 'roles.delete';
export const permissionCategories: Record<string, string[]> = {
  'Signup Forms': ['forms.view', 'forms.create', 'forms.edit', 'forms.delete'],
  'Email Templates': ['templates.view', 'templates.create', 'templates.edit', 'templates.delete'],
  'Mailing Lists': ['lists.view', 'lists.create', 'lists.edit', 'lists.delete', 'lists.manage_intake'],
  Campaigns: ['campaigns.view', 'campaigns.create', 'campaigns.edit', 'campaigns.send', 'campaigns.delete'],
  Submissions: ['submissions.view', 'submissions.export'],
  Subscriptions: ['subscriptions.view', 'subscriptions.manage', 'subscriptions.export'],
  Contacts: ['contacts.view', 'contacts.create', 'contacts.edit', 'contacts.delete', 'contacts.export'],
  Tags: ['tags.view', 'tags.create', 'tags.edit', 'tags.delete'],
  'Form Configurations': ['configs.view', 'configs.create', 'configs.edit', 'configs.delete'],
  'Team Users': ['users.view', 'users.invite', 'users.edit', 'users.delete', 'users.manage_roles'],
  Settings: ['settings.view', 'settings.edit'],
  'Audit Log': ['audit.view', 'audit.export'],
  'Roles & Permissions': ['roles.view', 'roles.create', 'roles.edit', 'roles.delete']
};
export const allPermissions: Permission[] = Object.values(permissionCategories).flat() as Permission[];
// ── Session persistence ──
const SESSION_KEY = 'configconsole_user_email';
function getStoredEmail(): string | null {
  try {
    return localStorage.getItem(SESSION_KEY);
  } catch {
    return null;
  }
}
function setStoredEmail(email: string) {
  try {
    localStorage.setItem(SESSION_KEY, email);
  } catch {}
}
function clearStoredEmail() {
  try {
    localStorage.removeItem(SESSION_KEY);
  } catch {}
}
// ── Workspace type ──
export interface Workspace {
  id: string;
  creatorId: string;
  name: string;
  region: string;
  subRegions: string[];
  logoUrl?: string;
  primaryColor?: string;
}
function teamToWorkspace(team: TeamRecord): Workspace {
  return {
    id: team.Team_ID,
    creatorId: team.ID,
    name: team.Team_Name,
    region: team.Team_Name,
    subRegions: (team.Team_Regions || []).map(r => r.Region_Name),
    logoUrl: team.Logo_Url || '',
    primaryColor: team.Primary_Color || '#2563eb'
  };
}
// ── App State ──
export type AppState = 'loading' | 'onboarding' | 'login' | 'ready' | 'unauthorized' | 'error';
// ── Context Type ──
interface WorkspaceContextType {
  // App state
  appState: AppState;
  loading: boolean;
  error: string | null;
  // Core scope
  currentWorkspace: Workspace;
  currentSubRegion: string | null;
  setCurrentWorkspace: (workspace: Workspace) => void;
  setCurrentSubRegion: (subRegion: string | null) => void;
  workspaces: Workspace[];
  // Current user
  currentUser: UserRecord & {
    role: string;
    name: string;
    email: string;
    id: string;
  };
  // Roles
  roles: RoleRecord[];
  // Permission helpers
  hasPermission: (permission: Permission) => boolean;
  getUserPermissions: (roleName?: string) => Permission[];
  // Session & lifecycle
  identifyUser: (email: string) => Promise<boolean>;
  completeOnboarding: () => Promise<void>;
  logout: () => void;
  refreshTeams: () => Promise<void>;
  refreshRoles: () => Promise<void>;
  // ── Backward-compatible stubs ──
  forms: any[];
  templates: any[];
  submissions: any[];
  subscriptions: any[];
  contacts: any[];
  confirmedContacts: any[];
  users: any[];
  allUsers: any[];
  formConfigs: any[];
  newsletters: any[];
  campaigns: any[];
  campaignTemplates: any[];
  auditEvents: any[];
  tags: any[];
  addSubRegion: (s: string) => void;
  removeSubRegion: (s: string) => void;
  addContactCategory: (c: string) => void;
  removeContactCategory: (c: string) => void;
  createWorkspace: (w: any) => void;
  createUser: (u: any) => void;
  updateUserStatus: (id: number, status: string) => void;
  updateUserRole: (id: number, roleId: number) => void;
  deleteUser: (id: number) => void;
  saveFormConfig: (c: any) => void;
  deleteFormConfig: (id: number) => void;
  createNewsletter: (n: any) => void;
  updateNewsletterIntake: (id: number, status: string, msg?: string) => void;
  createCampaign: (c: any) => void;
  createCampaignTemplate: (t: any) => void;
  createTag: (t: any) => void;
  deleteTag: (id: number) => void;
  createContact: (c: any) => void;
  updateContact: (id: number, u: any) => void;
  deleteContact: (id: number) => void;
  getContactsByCategory: (cat: string) => any[];
  getConfirmedContactsByList: (id: number) => any[];
  getSubmissionsByTag: (tag: string) => any[];
  getSubmissionsByMailingList: (id: number) => any[];
  getSubscriptionsByMailingList: (id: number) => any[];
  getUnconfiguredForms: () => any[];
  getUnassignedForms: () => any[];
  duplicateForm: (id: number) => void;
  duplicateNewsletter: (id: number) => void;
  duplicateTemplate: (id: number) => void;
  logAuditEvent: (...args: any[]) => void;
  createRole: (r: any) => void;
  updateRole: (id: number, u: any) => void;
  deleteRole: (id: number) => void;
  getRoleById: (id: number) => any;
}
const WorkspaceContext = createContext<WorkspaceContextType | undefined>(undefined);
const FALLBACK_WORKSPACE: Workspace = {
  id: 'loading',
  creatorId: '',
  name: 'Loading...',
  region: '',
  subRegions: [],
  status: 'Active'
};
const FALLBACK_USER: UserRecord = {
  ID: '',
  User_Name: 'Loading...',
  Email: '',
  Role: 'Viewer',
  Team: '',
  Status: 'Active',
  Invited_By: '',
  Created_At: '',
  Last_Login: ''
};
export function WorkspaceProvider({
  children
}: {
  children: ReactNode;
}) {
  const [appState, setAppState] = useState<AppState>('loading');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [workspaces, setWorkspaces] = useState<Workspace[]>([]);
  const [currentWorkspace, setCurrentWorkspace] = useState<Workspace>(FALLBACK_WORKSPACE);
  const [currentSubRegion, setCurrentSubRegion] = useState<string | null>(null);
  const [currentUser, setCurrentUser] = useState<UserRecord>(FALLBACK_USER);
  const [roles, setRoles] = useState<RoleRecord[]>([]);
  const [allUsersCache, setAllUsersCache] = useState<UserRecord[]>([]);
  // ── Load initial data ──
  const loadInitialData = useCallback(async () => {
    setLoading(true);
    setError(null);
    setAppState('loading');
    try {
      const [teamsData, usersData, rolesData] = await Promise.all([teamsApi.list(), usersApi.list(), rolesApi.list()]);
      setAllUsersCache(usersData);
      setRoles(rolesData);
      // Map teams to workspaces — include all teams (Status field may not exist in Creator)
      const ws = teamsData.map(teamToWorkspace);
      setWorkspaces(ws);
      // ── Determine app state ──
      // Case 1: No users at all → onboarding required
      if (usersData.length === 0) {
        setAppState('onboarding');
        setLoading(false);
        return;
      }
      // Case 2: Users exist — check for stored session
      const storedEmail = getStoredEmail();
      if (storedEmail) {
        const matchedUser = usersData.find(u => u.Email.toLowerCase() === storedEmail.toLowerCase() && u.Status !== 'Suspended');
        if (matchedUser) {
          setCurrentUser(matchedUser);
          // Set workspace based on user's team
          if (ws.length > 0) {
            const userTeam = ws.find(w => w.name === matchedUser.Team);
            setCurrentWorkspace(userTeam || ws[0]);
          }
          setAppState('ready');
          setLoading(false);
          return;
        } else {
          // Stored email no longer valid — clear it
          clearStoredEmail();
        }
      }
      // Case 3: Users exist but no valid session → need login
      setAppState('login');
      setLoading(false);
    } catch (err) {
      console.error('Failed to load workspace data:', err);
      setError(err instanceof Error ? err.message : 'Failed to load data');
      setAppState('error');
      setLoading(false);
    }
  }, []);
  useEffect(() => {
    loadInitialData();
  }, [loadInitialData]);
  // ── Identify user (login) ──
  const identifyUser = useCallback(async (email: string): Promise<boolean> => {
    // Check cached users first
    let users = allUsersCache;
    if (users.length === 0) {
      // Fetch fresh if cache is empty
      users = await usersApi.list();
      setAllUsersCache(users);
    }
    const matched = users.find(u => u.Email.toLowerCase() === email.toLowerCase() && u.Status !== 'Suspended');
    if (!matched) return false;
    // Check if suspended
    if (matched.Status === 'Suspended') return false;
    setCurrentUser(matched);
    setStoredEmail(email);
    // Set workspace
    if (workspaces.length > 0) {
      const userTeam = workspaces.find(w => w.name === matched.Team);
      setCurrentWorkspace(userTeam || workspaces[0]);
    }
    setAppState('ready');
    return true;
  }, [allUsersCache, workspaces]);
  // ── Complete onboarding ──
  const completeOnboarding = useCallback(async () => {
    // Reload everything from Creator
    await loadInitialData();
  }, [loadInitialData]);
  // ── Logout ──
  const logout = useCallback(() => {
    clearStoredEmail();
    setCurrentUser(FALLBACK_USER);
    setCurrentWorkspace(FALLBACK_WORKSPACE);
    setCurrentSubRegion(null);
    setAppState('login');
  }, []);
  // ── Refresh helpers ──
  const refreshTeams = useCallback(async () => {
    try {
      const teamsData = await teamsApi.list();
      const ws = teamsData.map(teamToWorkspace);
      setWorkspaces(ws);
      const updated = ws.find(w => w.id === currentWorkspace.id);
      if (updated) setCurrentWorkspace(updated);
    } catch (err) {
      console.error('Failed to refresh teams:', err);
    }
  }, [currentWorkspace.id]);
  const refreshRoles = useCallback(async () => {
    try {
      const rolesData = await rolesApi.list();
      setRoles(rolesData);
    } catch (err) {
      console.error('Failed to refresh roles:', err);
    }
  }, []);
  // ── Permission helpers ──
  const getUserPermissions = useCallback((roleName?: string): Permission[] => {
    const name = roleName || currentUser.Role;
    if (name === 'Super Admin') return allPermissions;
    const role = roles.find(r => r.Role_Name === name);
    if (!role?.Role_Permissions) return [];
    return role.Role_Permissions.map(p => p.Permission_Key as Permission);
  }, [currentUser.Role, roles]);
  const hasPermission = useCallback((permission: Permission): boolean => {
    if (currentUser.Role === 'Super Admin') return true;
    const perms = getUserPermissions();
    return perms.includes(permission);
  }, [currentUser.Role, getUserPermissions]);
  // ── Backward-compatible stubs ──
  const noop = () => {};
  const emptyArr = () => [] as any[];
  const compatUser = {
    ...currentUser,
    role: currentUser.Role,
    name: currentUser.User_Name,
    email: currentUser.Email,
    id: currentUser.ID
  };
  return <WorkspaceContext.Provider value={{
    appState,
    loading,
    error,
    currentWorkspace,
    currentSubRegion,
    setCurrentWorkspace,
    setCurrentSubRegion,
    workspaces,
    currentUser: compatUser as any,
    roles,
    hasPermission,
    getUserPermissions,
    identifyUser,
    completeOnboarding,
    logout,
    refreshTeams,
    refreshRoles,
    forms: [],
    templates: [],
    submissions: [],
    subscriptions: [],
    contacts: [],
    confirmedContacts: [],
    users: [],
    allUsers: [],
    formConfigs: [],
    newsletters: [],
    campaigns: [],
    campaignTemplates: [],
    auditEvents: [],
    tags: [],
    addSubRegion: noop,
    removeSubRegion: noop,
    addContactCategory: noop,
    removeContactCategory: noop,
    createWorkspace: noop,
    createUser: noop,
    updateUserStatus: noop,
    updateUserRole: noop,
    deleteUser: noop,
    saveFormConfig: noop,
    deleteFormConfig: noop,
    createNewsletter: noop,
    updateNewsletterIntake: noop,
    createCampaign: noop,
    createCampaignTemplate: noop,
    createTag: noop,
    deleteTag: noop,
    createContact: noop,
    updateContact: noop,
    deleteContact: noop,
    getContactsByCategory: emptyArr,
    getConfirmedContactsByList: emptyArr,
    getSubmissionsByTag: emptyArr,
    getSubmissionsByMailingList: emptyArr,
    getSubscriptionsByMailingList: emptyArr,
    getUnconfiguredForms: emptyArr,
    getUnassignedForms: emptyArr,
    duplicateForm: noop,
    duplicateNewsletter: noop,
    duplicateTemplate: noop,
    logAuditEvent: noop,
    createRole: noop,
    updateRole: noop,
    deleteRole: noop,
    getRoleById: () => undefined
  }} data-id="element-1026">
      {children}
    </WorkspaceContext.Provider>;
}
export function useWorkspace() {
  const context = useContext(WorkspaceContext);
  if (context === undefined) {
    throw new Error('useWorkspace must be used within a WorkspaceProvider');
  }
  return context;
}